import { defineStore } from 'pinia'
import releasesApi from '@/helpers/releasesApi'
import salesSplitsApi from '@/helpers/salesSplitsApi'
import dayjs from 'dayjs'
import { AdvanceType } from '@/constants'

export const useAdvanceStore = defineStore('advance', {
  state: () => ({
    advanceBalance: <AdvanceBalance | null> null,
    advanceHistory: <AdvanceHistory> {
      data: <AdvanceTransaction[]> [],
      meta: <Pagination | null> null,
    },
    advanceReleaseTitles: <AdvanceReleaseTitles> {},
    advanceAssetTitles: <AdvanceAssetTitles> {},
    advanceReleases: <Release[]> [],
  }),

  getters: {
    advanceTransactions: state => state.advanceHistory.data,
    advanceGroupedTransactions: state => {
      const map = new Map<string, AdvanceGroupedTransaction>();

      state.advanceHistory.data
        .filter(transaction => !(transaction.endsAt && Number(transaction.bundledAmount) === 0))
        .forEach(transaction => {
          // Sort entity IDs so different ordering still groups together
          const entityIds = transaction.entities.map(e => e.id).sort((a, b) => a - b)

          let key = `${transaction.type}|${entityIds.join(',')}`

          if (transaction.type === AdvanceType.asset) {
            const allLoaded = entityIds.every(id => state.advanceAssetTitles[id] !== undefined)

            if (allLoaded) {
              const titles = Array.from(new Set(entityIds
                .map(id => state.advanceAssetTitles[id])
                .filter((title): title is string => !!title)))
                .sort()

              key = `${transaction.type}|${titles.join(',')}`
            }
          }

          let group = map.get(key)

          if (!group) {
            group = {
              type: transaction.type,
              issueDate: transaction.createdAt,
              entityIds,
              transactions: [],
            }
            map.set(key, group)
          } else {
            group.entityIds = Array.from(new Set([...group.entityIds, ...entityIds])).sort((a, b) => a - b)
          }

          group.transactions.push(transaction)

          if (dayjs(transaction.createdAt).isAfter(dayjs(group.issueDate))) {
            group.issueDate = transaction.createdAt
          }
        })

      return [...map.values()]
    },
    getReleaseById: state => (id: number) => state.advanceReleases.find(release => release.id === id),
  },

  actions: {
    async getAdvanceBalance() {
      const { data } = await salesSplitsApi.get('recoup_commissions/total')
      this.advanceBalance = data
    },

    async getAdvanceHistory(page: number) {
      const { data } = await salesSplitsApi.get('recoup_commissions', {
        params: {
          pagination: true,
          page,
          perPage: 50,
        },
      })
      this.advanceHistory = data
    },

    async getReleases(releaseIds: number[]): Promise<Release[]> {
      if (releaseIds.length === 0) {
        return [];
      }

      const perPage = 100;

      // Fetch first page
      const { data: { data: firstPageReleases, meta } } = await releasesApi.get('/releases', {
        params: {
          id: releaseIds,
          page: 1,
          perPage,
        },
      });

      const { pagesCount } = meta;

      if (pagesCount <= 1) {
        return firstPageReleases || [];
      }

      const remainingPageStart = 2
      const remainingPageNumbers = Array.from(
        { length: pagesCount - 1 },
        (_, index) => index + remainingPageStart,
      );

      const pagePromises = remainingPageNumbers.map(page => releasesApi.get('/releases', {
        params: {
          id: releaseIds,
          page,
          perPage,
        },
      }))

      const responses = await Promise.all(pagePromises);

      const subsequentReleases = responses.flatMap(response => response.data.data || []);

      return [...(firstPageReleases || []), ...subsequentReleases];
    },

    async getAllMissingReleases(releaseIds: number[]): Promise<void> {
      const missingReleaseIds = releaseIds.filter(releaseId => !(releaseId in this.advanceReleaseTitles));

      if (missingReleaseIds.length === 0) {
        return;
      }

      const fetchedReleases: Release[] = await this.getReleases(missingReleaseIds)

      this.advanceReleases = [
        ...this.advanceReleases,
        ...fetchedReleases,
      ]

      const releasesById = fetchedReleases.reduce((map, release) => ({
        ...map,
        [release.id]: release,
      }), {} as Record<number, Release>);

      missingReleaseIds.forEach(releaseId => {
        const foundRelease = releasesById[releaseId];
        const title = (foundRelease && foundRelease.title)
          ? foundRelease.title
          : '[Release Not Found]';

        this.advanceReleaseTitles = {
          ...this.advanceReleaseTitles,
          [releaseId]: title,
        }
      });
    },

    async getTracks(trackIds: number[]): Promise<Track[]> {
      if (trackIds.length === 0) {
        return []
      }

      const perPage = trackIds.length

      const { data: { data: tracks } } = await releasesApi.get('/tracks', {
        params: {
          id: trackIds,
          page: 1,
          perPage,
        },
      })

      return tracks || []
    },

    async getAllMissingTracks(trackIds: number[]): Promise<void> {
      const missingTrackIds = trackIds.filter(trackId => !(trackId in this.advanceAssetTitles))

      if (missingTrackIds.length === 0) {
        return
      }

      const fetchedTracks = await this.getTracks(missingTrackIds)
      const tracksById = fetchedTracks.reduce((map, track) => ({
        ...map,
        [track.id]: track,
      }), {} as Record<number, Track>)

      missingTrackIds.forEach(trackId => {
        const track = tracksById[trackId]
        const title = track?.isrc
          ? `${track.title} (${track.isrc})`
          : track?.title

        this.advanceAssetTitles = {
          ...this.advanceAssetTitles,
          [trackId]: title || '[Track Not Found]',
        }
      })
    },
  },
})
